import { useTranslate } from '@tolgee/react';
import Clipboard from 'clipboard';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CopySvg } from '../../../assets/icons';
import DownloadSvg from '../../../assets/icons/DownloadSvg';
import FoldedHandsSVG from '../../../assets/icons/FoldedHands';
import theme from '../../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useExportExcelResults from '../../../hooks/useExportExcelResults';
import { useMetrics } from '../../../hooks/useMetrics';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import BaseModal from '../BaseModal';

const ShareReportModal = ({ gameId }: { gameId: string }) => {
  const templateRef = useRef(new Clipboard('#copy_url_template'));
  const [copyTemplateSuccess, setCopyTemplateSuccess] = useState(false);
  const { t } = useTranslate();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const isLargerThan1100 = useBreakPoint(1100);

  const metrics = useMetrics();
  const { accessBadge: userAccessBadge } = useAccessBadge();

  // Paywall functionality for freemium users
  const { promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  // Check if user is on freemium plan
  const isFreemiumUser =
    userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.freemium;
  useEffect(() => {
    const clip = templateRef.current;
    clip.on('success', () => {
      setCopyTemplateSuccess(true);
    });

    return () => {
      clip.destroy();
    };
  }, []);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copyTemplateSuccess) {
        metrics.logEvent('Report.Share.Copy.Success', {
          gameId: gameId,
          from: 'Reports',
        });
        // Paused until decision on product events
        // trackAttioEvent('results_shared', {
        //   organizationId: userAccessBadge?.organization?.id,
        //   userId: userAccessBadge?.user?.id,
        //   userRole: userAccessBadge?.user?.profileInfo?.role,
        //   email: userAccessBadge?.user?.email,
        //   firstName: userAccessBadge?.user?.firstName,
        //   lastName: userAccessBadge?.user?.lastName,
        // });
        setCopyTemplateSuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copyTemplateSuccess, metrics, gameId]);

  const { urlTenantId: tenantIdRouter } = useRouterMatch();

  const shareUrl = `${window.location.origin}/${tenantIdRouter}/games/${gameId}/live/results`;
  const {
    download: downloadExcel,
    isLoading: isDownloadingExcel,
    isErrorDownloading,
    reset: resetDownload,
  } = useExportExcelResults(gameId);

  useEffect(() => {
    if (isErrorDownloading) {
      setShowErrorModal(true);
      resetDownload;
    }
  }, [isErrorDownloading]);

  return (
    <div
      style={{
        width: '100%',
        padding: '16px 20px',
      }}
    >
      {paywallComponent}
      <>
        <BaseModal open={showErrorModal} onCancel={() => setShowErrorModal(false)}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
              <FoldedHandsSVG />
            </IconWrapper>

            <ErrorMessage>
              {isErrorDownloading && (
                <>
                  <ErrorMessage style={{ fontSize: '16px' }}>
                    {isLargerThan1100
                      ? 'Oops, there was a problem creating the Excel file, please try again. If the problem persists, please contact us in the chat, in the bottom right of the screen.'
                      : 'Oops, there was a problem creating the Excel file, please try again. If the problem persists, please contact us by clicking on the help button found in the menu at the top.'}
                  </ErrorMessage>
                </>
              )}
            </ErrorMessage>
            <Button
              styles={{ marginTop: 20 }}
              width="120px"
              onClick={() => setShowErrorModal(false)}
            >
              Ok
            </Button>
          </div>
        </BaseModal>
        <h2>Share this report</h2>
        <Title $fontSize={16}>{t('Share with link')}</Title>
        <p style={{ fontWeight: 'bold', color: '#5F6368' }}>
          Anyone with access to this link will see the slides with all activity data. No
          student names are shown.
        </p>
        <ShareButton
          $buttonColor={theme.colors.orange}
          icon={<CopySvg />}
          id="copy_url_template"
          data-clipboard-text={isFreemiumUser ? '#' : shareUrl}
          onClick={() => {
            if (isFreemiumUser) {
              promptPaywall({
                label: 'Report page: Share report url',
                description: 'Report sharing functionality',
              });
            }
          }}
        >
          {copyTemplateSuccess ? t('Link copied') : t('Copy link')}
        </ShareButton>
        <Divider style={{ width: 'calc(100% - 20px)', margin: 10 }} />
        <Title style={{ marginTop: 20 }} $fontSize={16}>
          Download
        </Title>
        <p style={{ color: '#5F6368' }}>
          The downloaded report will only contain the activity data and student data.
        </p>
        <ShareButton
          $buttonColor={theme.colors.green}
          icon={<DownloadSvg />}
          loading={isDownloadingExcel}
          onClick={() => {
            if (isFreemiumUser) {
              promptPaywall({
                label: 'Report page: Download report',
                description: 'Report download functionality',
              });
              return;
            }
            downloadExcel();
          }}
        >
          {isDownloadingExcel ? 'Downloading report' : 'Download report'}
        </ShareButton>
      </>
    </div>
  );
};

const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

const ShareButton = styled(Button)<{ $buttonColor: string }>`
  color: ${theme.colors.white};
  background-color: ${({ $buttonColor }) => $buttonColor};
  width: 100%;
  border: none;
  &:hover,
  &:active,
  &:focus {
    color: ${theme.colors.white};
    background-color: ${({ $buttonColor }) => $buttonColor};
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.grey};
  width: 100%;
  position: absolute;
  left: 0;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

export default ShareReportModal;
